import detectIndent from 'detect-indent';

import { Block, type Change, Text, type Value } from '@gitbook/slate';

import { BLOCK_CODE, BLOCK_CODE_LINE, type BlockCodeData } from '../types';

/**
 * Return the current code block, from current selection or from a node key.
 */
export function getCurrentCode(value: Value, key?: string): Block | null {
    const { document } = value;

    let currentBlock: Block | null;

    if (key) {
        currentBlock = value.document.getDescendant<Block>(key);
    } else {
        // @ts-expect-error
        if (!value.selection.startKey) {
            return null;
        }
        currentBlock = value.startBlock;
    }

    // The structure is always code_block -> code_line -> text
    // So the parent of the currentBlock should be the code_block
    const parent = document.getParent(currentBlock!.key);

    if (Block.isBlock(parent) && parent.type === BLOCK_CODE) {
        return parent;
    }
    return null;
}

/**
 * Detect indentation in a text
 */
export function getIndent(text: string, defaultValue = '    '): string {
    return detectIndent(text).indent || defaultValue;
}

/**
 * Deserialize a text into a code block
 */
export function createCodeBlock(
    lines: string | Block[],
    { syntax, ...restData }: BlockCodeData = {}
): Block {
    const code = Block.create({
        type: BLOCK_CODE,
        nodes: typeof lines === 'string' ? createCodeLines(lines) : lines,
        data: {
            ...restData,
            ...(!syntax || isPlainTextSyntax(syntax) ? {} : { syntax }),
        },
    });

    return code;
}

/**
 * Deserialize a text into a set of lines.
 */
function createCodeLines(text: string): Block[] {
    return splitTextByNewLines(text).map((line) =>
        Block.create({
            type: BLOCK_CODE_LINE,
            data: {},
            nodes: [Text.create(line)],
        })
    );
}

/**
 * Split a text by newline chars into a set of lines.
 */
export function splitTextByNewLines(text: string): string[] {
    return text.split(/\r?\n/);
}

/**
 * Get the inner text of a code block, excluding annotations.
 * Each codeline is separated by a newline.
 * In a diff, the block holds the lines of both versions: `diffSide` picks which one to read,
 * the new one by default.
 */
export function getCodeText(code: Block, options: { diffSide?: 'before' | 'after' } = {}): string {
    const otherSideOnly = options.diffSide === 'before' ? 'added' : 'deleted';
    return code.nodes
        .filter((node) => !Block.isBlock(node) || node.data.get('diff') !== otherSideOnly)
        .map((node) => {
            if (!Block.isBlock(node)) {
                return '';
            }
            const texts = node.getTexts(false);
            return texts.map((t) => t.text).join('');
        })
        .join('\n');
}

/**
 * Check if a syntax is equivalent to the default plain text one.
 */
export function isPlainTextSyntax(syntax: string): boolean {
    return syntax === 'plain' || syntax === 'text';
}

export function splitCodeLine(change: Change) {
    const { startBlock } = change.value;
    if (!change.value.isCollapsed || !startBlock) {
        return;
    }
    const currentLineText = startBlock?.text;
    const indent = getIndent(currentLineText, '');
    return change.splitBlock().insertText(indent).focus();
}

/**
 * Check if the node is a code block and it has wrapped code block lines
 */
export function isWrappedCodeBlock(node: Block): boolean {
    return node.type === BLOCK_CODE && node.data.get('overflow') === 'wrap';
}
